import api from './api'
import type { LegalRepresentative, User } from '../types'

export interface CheckDocumentPayload {
  document_number: string
  id_type_document?: number
}

export interface CheckDocumentResponse {
  exists: boolean
  message: string
}

export interface CheckEmailPayload {
  email: string
}

export interface CheckEmailResponse {
  exists: boolean
  message: string
}

export interface SendVerificationCodePayload {
  email: string
}

export interface SendVerificationCodeResponse {
  sent: boolean
  message: string
}

export interface VerifyEmailCodePayload {
  email: string
  code: string
}

export interface VerifyEmailCodeResponse {
  verified: boolean
  message: string
}

export interface ForgotPasswordPayload {
  email: string
}

export interface VerifyRecoveryCodePayload {
  email: string
  code: string
}

export interface ResetPasswordPayload {
  email: string
  code: string
  password: string
}

export interface LoginPayload {
  email: string
  password: string
  _hp_verification?: string
}

export interface RegisterPayload {
  email: string
  password: string
  first_name: string
  middle_name?: string | null
  last_name?: string | null
  second_last_name?: string | null
  birth_date?: string | null
  gender?: string | null
  business_name?: string | null
  company_name?: string | null
  company_duration_years?: number | null
  constitution_date?: string | null
  mercantile_registry_name?: string | null
  mercantile_registry_date?: string | null
  mercantile_registry_number?: string | null
  mercantile_registry_volume?: string | null
  mercantile_registry_file_number?: string | null
  legal_representatives?: LegalRepresentative[] | null
  legal_rep_first_name?: string | null
  legal_rep_last_name?: string | null
  legal_rep_document_number?: string | null
  legal_rep_rif?: string | null
  legal_rep_email?: string | null
  legal_rep_phone?: string | null
  legal_rep_position?: string | null
  legal_rep_nationality?: string | null
  legal_rep_2_first_name?: string | null
  legal_rep_2_last_name?: string | null
  legal_rep_2_document_number?: string | null
  legal_rep_2_rif?: string | null
  legal_rep_2_email?: string | null
  legal_rep_2_phone?: string | null
  legal_rep_2_position?: string | null
  legal_rep_2_nationality?: string | null
  id_type_client: number
  id_type_document: number
  document_number: string
  phone: string
  work_phone?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
}

export interface AuthResponse {
  user: User
  accessToken: string
  refreshToken?: string
  expiresIn?: number
}

/**
 * Centralized Auth API Provider
 * Encapsulates all backend authentication requests and standard security parameters.
 */
export const authProvider = {
  checkDocument(payload: CheckDocumentPayload) {
    return api.post<CheckDocumentResponse>('/auth/check-document', payload)
  },

  checkEmail(payload: CheckEmailPayload) {
    return api.post<CheckEmailResponse>('/auth/check-email', payload)
  },

  sendVerificationCode(payload: SendVerificationCodePayload) {
    return api.post<SendVerificationCodeResponse>('/auth/send-verification-code', payload)
  },

  verifyEmailCode(payload: VerifyEmailCodePayload) {
    return api.post<VerifyEmailCodeResponse>('/auth/verify-email-code', payload)
  },

  forgotPassword(payload: ForgotPasswordPayload) {
    return api.post<{ message: string }>('/auth/forgot-password', payload)
  },

  verifyRecoveryCode(payload: VerifyRecoveryCodePayload) {
    return api.post<{ token?: string; message?: string }>('/auth/verify-recovery-code', payload)
  },

  resetPassword(payload: ResetPasswordPayload) {
    return api.post<{ message: string }>('/auth/reset-password', payload)
  },

  login(payload: LoginPayload) {
    return api.post<AuthResponse>('/auth/login', payload)
  },

  register(payload: RegisterPayload) {
    return api.post<AuthResponse>('/auth/register', payload)
  },

  logout() {
    return api.post<{ message: string }>('/auth/logout')
  },

  getProfile() {
    return api.get<User>('/me')
  },

  acceptTerms() {
    return api.post<{ message: string; terms_accepted_at: string }>('/accept-terms')
  },
}

export default authProvider
